import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, SubtitleStyleSpan } from '@packages/global';
import { CheckIcon, PencilSquareIcon, UsersIcon } from '@heroicons/react/24/outline';
import { videoClipApi } from '../../../services/user';
import { useToast } from '../../../stores/toastStore';
import SubtitleKeywordStyler from './components/SubtitleKeywordStyler';
import FormModal from '../../common/FormModal';

interface StoryboardTabProps {
  videoClip: IVideoClip;
  refreshVideoClip?: (clipId: string) => Promise<void>;
}

export default function StoryboardTab({ videoClip, refreshVideoClip }: StoryboardTabProps) {
  const [formData, setFormData] = useState({
    description: '',
    script: '',
    subtitleStyleSpans: [] as SubtitleStyleSpan[],
    speaker: '',
    sceneName: '',
    structure: '',
    cameraWork: '',
    transition: ''
  });
  const [saving, setSaving] = useState(false);
  const [subtitleModalOpen, setSubtitleModalOpen] = useState(false);
  const [subtitleDraft, setSubtitleDraft] = useState({
    script: '',
    subtitleStyleSpans: [] as SubtitleStyleSpan[],
  });
  const { success, error } = useToast();
  const { t } = useTranslation(['editor', 'common']);

  // 切换片段时更新表单数据
  useEffect(() => {
    setFormData({
      description: videoClip.description || '',
      script: videoClip.script || '',
      subtitleStyleSpans: Array.isArray(videoClip.subtitleStyleSpans) ? videoClip.subtitleStyleSpans : [],
      speaker: videoClip.speaker || '',
      sceneName: videoClip.sceneName || '',
      structure: videoClip.structure || '',
      cameraWork: videoClip.cameraWork || '',
      transition: videoClip.transition || ''
    });
    setSubtitleDraft({
      script: videoClip.script || '',
      subtitleStyleSpans: Array.isArray(videoClip.subtitleStyleSpans) ? videoClip.subtitleStyleSpans : [],
    });
  }, [videoClip.id]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const normalizedFormData = {
        ...formData,
        subtitleStyleSpans:
          formData.script !== (videoClip.script || '')
            ? []
            : formData.subtitleStyleSpans,
      };
      await videoClipApi.updateVideoClip(videoClip.id, normalizedFormData);
      setFormData(normalizedFormData);
      success(t('storyboardTab.saveSuccess'));
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
    } catch (err) {
      error(t('storyboardTab.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenSubtitleModal = () => {
    setSubtitleDraft({
      script: formData.script,
      subtitleStyleSpans: formData.subtitleStyleSpans,
    });
    setSubtitleModalOpen(true);
  };

  const handleQuickScriptChange = (script: string) => {
    setFormData((prev) => ({
      ...prev,
      script,
      subtitleStyleSpans: [],
    }));
  };

  const handleSaveSubtitleModal = async () => {
    try {
      setSaving(true);
      const updatePayload = {
        script: subtitleDraft.script,
        subtitleStyleSpans: subtitleDraft.subtitleStyleSpans,
      };
      await videoClipApi.updateVideoClip(videoClip.id, updatePayload);
      setFormData((prev) => ({
        ...prev,
        ...updatePayload,
      }));
      success(t('storyboardTab.saveSuccess'));
      setSubtitleModalOpen(false);
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
    } catch (err) {
      error(t('storyboardTab.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* 可滚动的表单区域 */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="space-y-4">
          {/* 角色信息区域 */}
          <div className="bg-gray-800 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <UsersIcon className="h-4 w-4" />
              <span>{t('storyboardForm.characterInfo')}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('storyboardForm.speaker')}</label>
                <input
                  type="text"
                  value={formData.speaker}
                  onChange={(e) => setFormData({ ...formData, speaker: e.target.value })}
                  className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  placeholder={t('storyboardForm.speakerPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('storyboardForm.sceneName')}</label>
                <input
                  type="text"
                  value={formData.sceneName}
                  onChange={(e) => setFormData({ ...formData, sceneName: e.target.value })}
                  className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  placeholder={t('storyboardForm.sceneNamePlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* 内容描述 */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('storyboardTab.imageDescription')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder={t('storyboardTab.imageDescriptionPlaceholder')}
            />
          </div>

          {/* 台词脚本 */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('storyboardTab.dialogueScript')}</label>
            <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-3 space-y-3">
              <textarea
                value={formData.script}
                onChange={(e) => handleQuickScriptChange(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={t('storyboardTab.dialogueScriptPlaceholder')}
              />
              <div className="flex items-center gap-3">
                <div className="text-xs text-gray-400 flex-1 min-w-0">
                  {formData.subtitleStyleSpans.length > 0
                    ? t('storyboardTab.keywordEffectsCount', { count: formData.subtitleStyleSpans.length })
                    : t('storyboardTab.quickEditHint')}
                </div>
                <button
                  type="button"
                  onClick={handleOpenSubtitleModal}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-100 px-3 py-2 text-sm transition-colors flex-shrink-0 min-w-[88px] justify-center whitespace-nowrap"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  {t('storyboardTab.subtitleEffects')}
                </button>
              </div>
            </div>
          </div>

          {/* 镜头技术参数 */}
          <div className="bg-gray-800 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <span>{t('storyboardForm.shotParams')}</span>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">{t('storyboardTab.shotStructure')}</label>
              <input
                type="text"
                value={formData.structure}
                onChange={(e) => setFormData({ ...formData, structure: e.target.value })}
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                placeholder={t('storyboardTab.shotStructurePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">{t('storyboardTab.cameraMovement')}</label>
              <input
                type="text"
                value={formData.cameraWork}
                onChange={(e) => setFormData({ ...formData, cameraWork: e.target.value })}
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                placeholder={t('storyboardTab.cameraMovementPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">{t('storyboardTab.transitionEffect')}</label>
              <input
                type="text"
                value={formData.transition}
                onChange={(e) => setFormData({ ...formData, transition: e.target.value })}
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                placeholder={t('storyboardTab.transitionEffectPlaceholder')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 底部保存按钮 */}
      <div className="flex-shrink-0 border-t border-gray-800 bg-gray-800/30 px-4 py-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-500 text-white px-4 py-3 rounded-lg disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors font-medium"
        >
          <CheckIcon className="w-5 h-5" />
          {saving ? t('common:loading') : t('storyboardTab.confirmSave')}
        </button>
      </div>

      <FormModal
        isOpen={subtitleModalOpen}
        onClose={() => setSubtitleModalOpen(false)}
        title={t('storyboardTab.editSubtitle')}
        size="xl"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setSubtitleModalOpen(false)}
              className="px-4 py-2 rounded-lg bg-bg-elevated text-gray-300 hover:text-gray-50 transition-colors"
            >
              {t('common:cancel')}
            </button>
            <button
              type="button"
              onClick={handleSaveSubtitleModal}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? t('common:loading') : t('common:confirm')}
            </button>
          </>
        )}
      >
        <SubtitleKeywordStyler
          value={subtitleDraft.script}
          spans={subtitleDraft.subtitleStyleSpans}
          onTextChange={(script) => setSubtitleDraft((prev) => ({ ...prev, script }))}
          onSpansChange={(subtitleStyleSpans) => setSubtitleDraft((prev) => ({ ...prev, subtitleStyleSpans }))}
        />
      </FormModal>
    </div>
  );
}
